import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getToken, getRefreshToken } from '@/utils/common';
import { ForgotPasswordResponseData, LoginSessionChallengeState, TwoFAType } from '@/types/auth';

export type AuthState = {
  user: UserProfile | null;
  token?: string | null;
  refreshToken?: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userDetail?: UserDetailType;
  forgotPasswordData: ForgotPasswordResponseData | null;
  loginSessionChallenge: LoginSessionChallengeState | null;
  data2fa?: TwoFAType;
  dataUserAccountAccess?: UserAccountAccessType;
};

export const authInitialState: AuthState = {
  user: null,
  token: getToken(),
  refreshToken: getRefreshToken(),
  isAuthenticated: !!getToken(),
  isLoading: false,
  forgotPasswordData: null,
  loginSessionChallenge: null,
};

const slice = createSlice({
  name: 'auth',
  initialState: authInitialState,
  reducers: {
    clearAuthDataAction(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
    },

    setDataAndTokenAction(
      state,
      {
        payload,
      }: PayloadAction<{
        user?: UserProfile | null;
        token?: string | null;
        refreshToken?: string | null;
        isAuthenticated?: boolean;
      }>,
    ) {
      if (payload.user !== undefined) {
        state.user = payload.user;
      }
      if (payload.token !== undefined) {
        state.token = payload.token;
      }
      if (payload.refreshToken !== undefined) {
        state.refreshToken = payload.refreshToken;
      }
      if (payload.isAuthenticated !== undefined) {
        state.isAuthenticated = payload.isAuthenticated;
      }
    },

    setLoadingAction(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },

    setUserAction(state, { payload }: PayloadAction<UserProfile | null>) {
      state.user = payload;
      state.isAuthenticated = !!payload;
    },

    updateTokensAction(
      state,
      { payload }: PayloadAction<{ token?: string | null; refreshToken?: string | null }>,
    ) {
      if (payload.token !== undefined) {
        state.token = payload.token;
      }
      if (payload.refreshToken !== undefined) {
        state.refreshToken = payload.refreshToken;
      }
      state.isAuthenticated = !!payload.token;
    },

    setUserDetailAction(state, { payload }: PayloadAction<{ userDetail: UserDetailType }>) {
      state.userDetail = payload.userDetail;
    },

    setData2faAction(state, { payload }: PayloadAction<{ data2fa: TwoFAType }>) {
      state.data2fa = payload.data2fa;
    },

    setForgotPasswordDataAction(
      state,
      { payload }: PayloadAction<ForgotPasswordResponseData | null>,
    ) {
      state.forgotPasswordData = payload;
    },

    setLoginSessionChallengeAction(
      state,
      { payload }: PayloadAction<LoginSessionChallengeState | null>,
    ) {
      state.loginSessionChallenge = payload;
    },

    setDataUserAccountAccessAction(
      state,
      { payload }: PayloadAction<{ dataUserAccountAccess: UserAccountAccessType }>,
    ) {
      state.dataUserAccountAccess = payload.dataUserAccountAccess;
    },
  },
});

export const {
  clearAuthDataAction,
  setDataAndTokenAction,
  setLoadingAction,
  setUserAction,
  updateTokensAction,
  setUserDetailAction,
  setData2faAction,
  setForgotPasswordDataAction,
  setLoginSessionChallengeAction,
  setDataUserAccountAccessAction,
} = slice.actions;
export default slice.reducer;
